import { useState } from "react";
import { Button, Modal, Tooltip, Flex } from "@mantine/core";
import { useMutation, useQuery } from "@apollo/client";
import { notifications } from "@mantine/notifications";
import { useNavigate } from "react-router-dom";
import { Notifications } from "@mantine/notifications";
import { useDebounce } from "use-debounce";

import { STYLE, TOGGLE_FEATURE } from "../types/constants";
import {
  ItemType,
  OrderStatus,
  TranslationsAvailable,
  UserType,
  ShippingType,
  CarrierType,
} from "../types/enums";
import {
  POPULATE_DATA_FROM_3SHAPE,
  POPULATE_DATA_FROM_MEDITLINK,
  REFRESH_PASTILLE,
  UPDATE_ORDER,
  GET_DELIVERY_PROOF,
} from "../gql/orders";
import { isUserStaff, isUserProvider } from "../utils/user.utils";
import { GET_ALL_USERS } from "../gql/users";
import RedoModal from "./modals/RedoModal";
import UpdateDeliveryDateModal from "./modals/updateDeliveryDateModal";
import UpdateMaterialAndLotModal from "./modals/updateMaterialAndLotModal";
import SendOrderToSupplierModal from "./modals/sendOrderToSupplierModal";
import ChangeDentistModal from "./modals/ChangeDentistModal";
import PickupHistory from "./modals/pickupHistory";
import CreatePickup from "./modals/createPickup";
import {
  IconArchive,
  IconArrowLeftFromArc,
  IconCalendarCog,
  IconEdit,
  IconHandStop,
  IconListDetails,
  IconLogs,
  IconMailbox,
  IconPlaneDeparture,
  IconProgress,
  IconRepeat,
  IconSortAscendingShapes,
  IconStatusChange,
  IconTransfer,
  IconArrowIteration,
  IconArchiveOff,
  IconPrinter,
  IconCloudDown,
  IconChecklist,
  IconTruckReturn,
  IconHistory,
  IconTruckLoading,
  IconTool,
  IconList,
  IconSend,
  IconFile3d,
  IconTagsFilled,
} from "@tabler/icons-react";
import { ARCHIVE_MESSAGES_FOR_ORDER, SEND_MESSAGE } from "../gql/chat";
import { sendMailToProvider } from "../gql/email";
import { GENERATE_TRACEABILITY_DOCUMENT } from "../gql/billing";
import {
  createPDFFromArrayText,
  downloadFile,
  getValideFiles,
} from "../utils/file.utils";
import { downloadBase64File } from "../utils/files.utils";
import CarrierDeliveryInfo from "./modals/carrierDeliveryInfo";
import {
  CREATE_PICKUP,
  CREATE_MANUAL_PICKUP,
  ABORT_PICKUP,
  GET_PICKUP_AVAILABILITY,
} from "../gql/pickup";
import { Order, User } from "../types/interfaces";
import ShippingHistory from "./modals/shippingHistory";
import { hasAtLeastOneMaterialTraceability } from "../utils/material-traceability.utils";
import { GET_ALL_MATERIAL_TRACEABILITY_WHERE } from "../gql/materialTraceability";
import { ButtonMenu } from "./button/buttonMenu";
import { OrderHistoryItem } from "./OrderHistoryItem";
import {
  displayDeliveryStatusButton,
  getLastTrackingNumber,
  hasDeliveryTrackingNumber,
  hasPickupTrackingNumber,
  isIntegratedCarrier,
} from "../utils/shipping.utils";
import UploadDesignModal from "./modals/UploadDesignModal";
import { IntegrationType, IntegrationTypeEnum } from "@jasper/client-models";
import { willActivateOrder } from "../utils/order.utils";

const __TAKE__ = 50;

const HeaderOrderView = ({ order, user, refetch }) => {
  const [sendMail] = useMutation(sendMailToProvider);
  const [generateTracebilitySheet] = useMutation(
    GENERATE_TRACEABILITY_DOCUMENT
  );
  const [isUploadDesignModalOpen, setIsUploadDesignModalOpen] = useState(false);
  const [openMaterialAndLotModal, setOpenMaterialAndLotModal] = useState(false);
  const [openDeliveryDateModal, setOpenDeliveryDateModal] = useState(false);
  const [openRedoModal, setOpenRedoModal] = useState(false);
  const [openSendToSupplierModal, setOpenSendToSupplierModal] = useState(false);
  const [openModalOrderHistory, setOpenModalOrderHistory] = useState(false);
  const [changeDentistModalOpened, setChangeDentistModalOpened] =
    useState(false);
  const [searchDentistInput, setSearchDentistInput] = useState<string>("");
  const [isCarrierDeliveryModalOpened, setIsCarrierDeliveryModalOpened] =
    useState<boolean>(false);
  const [isShippingHistoryModalOpened, setIsShippingHistoryModalOpened] =
    useState<boolean>(false);
  const [isPickupHistoryModalOpened, setIsPickupHistoryModalOpened] =
    useState<boolean>(false);
  const [isCreatePickupModalOpened, setIsCreatePickupModalOpened] =
    useState<boolean>(false);
  const [shippedBackDate, setShippedBackDate] = useState<Date | null>(
    order?.shippedBackToProviderDate
      ? new Date(order.shippedBackToProviderDate)
      : null
  );
  const [searchDentistInputDebounce] = useDebounce(searchDentistInput, 500);

  const navigate = useNavigate();
  const lastTrackingNumber = getLastTrackingNumber(order?.shippings ?? []);

  const [populateDataFrom3Shape, { loading: isCommunicateDataLoading }] =
    useMutation(POPULATE_DATA_FROM_3SHAPE);
  const [populateDataFromMeditlink, { loading: isMeditLinkDataLoading }] =
    useMutation(POPULATE_DATA_FROM_MEDITLINK);
  const [updateOrder] = useMutation(UPDATE_ORDER);
  const [updateChat] = useMutation(ARCHIVE_MESSAGES_FOR_ORDER);
  const [refreshPastille] = useMutation(REFRESH_PASTILLE);
  const [getDeliveryProofGQL] = useMutation(GET_DELIVERY_PROOF);
  const [createPickupMutation] = useMutation(CREATE_PICKUP);
  const [createManualPickup] = useMutation(CREATE_MANUAL_PICKUP);
  const [abortPickupMutation] = useMutation(ABORT_PICKUP);
  const [sendChatMessage] = useMutation(SEND_MESSAGE);

  const { data: pickupAvailable } = useQuery(GET_PICKUP_AVAILABILITY, {
    variables: { orderId: order.id },
  });

  const downloadBagLabel = async (order: Order): Promise<void> => {
    try {
      const pdfBlob = await createPDFFromArrayText({
        data: [
          `Rx: ${order.orderReference}`,
          `Patient: ${order.patient?.lastName} ${order.patient?.firstName}`,
          `Praticien: ${order.user.firstName} ${order.user.lastName.charAt(0)}`,
          `Clinic code: ${order.user.userGroup.clinicNumber}`,
        ],
        dimensions: {
          height: (4.5 * 72) / 2.54,
          width: (10 * 72) / 2.54,
          size: 12,
          x: 10,
          y: 18,
        },
      });
      downloadFile(pdfBlob, `bag-label-${order.orderReference}.pdf`);
    } catch (e) {
      console.error(e);
      notifications.show({
        title: "Error",
        message: "Error while trying to download bag label",
        color: "red",
      });
    }
  };

  const populateExternalData = async ({
    orderId,
    integrationType,
    willActivateOrder,
  }: {
    orderId: string;
    integrationType: IntegrationType;
  }) => {
    try {
      if (isCommunicateDataLoading || isMeditLinkDataLoading) {
        notifications.show({
          title: `Already downloading ${integrationType} data, please wait`,
          color: "orange",
          message: "",
        });
        return;
      }

      notifications.show({
        title: `Downloading ${integrationType} data..`,
        color: "blue",
        message: "",
        loading: true,
        id: "loading-data-notification",
        autoClose: false,
      });

      switch (integrationType) {
        case IntegrationTypeEnum.THREESHAPE:
          await populateDataFrom3Shape({
            variables: {
              orderId: orderId,
            },
          });
          break;
        case IntegrationTypeEnum.MEDITLINK:
          await populateDataFromMeditlink({
            variables: {
              orderId: orderId,
            },
          });
          break;
      }

      notifications.hide("loading-data-notification");
      notifications.show({
        title: `Successfully downloaded ${integrationType} data`,
        color: "green",
        message: "",
      });
      refetch();
    } catch (e) {
      console.error(e);
      notifications.hide("loading-data-notification");
      notifications.show({
        title: `Error while trying to download ${integrationType} data`,
        color: "red",
        message: "",
      });
    }
  };

  const abortPickup = async (pickupId: string) => {
    try {
      notifications.show({
        title: "Aborting pickup request..",
        color: "blue",
        message: "",
        loading: true,
        id: "abort-pickup-notification",
        autoClose: false,
      });
      await abortPickupMutation({
        variables: {
          pickupId: pickupId,
        },
      });
      refetch();
      setIsPickupHistoryModalOpened(false);
      notifications.hide("abort-pickup-notification");
      notifications.show({
        title: "Pickup request aborted",
        color: "green",
        message: "",
      });
    } catch (e) {
      console.error(e);
      notifications.hide("abort-pickup-notification");
      notifications.show({
        title: "Error while trying to abort pickup, carrier already dispatched",
        color: "red",
        message: "",
      });
    }
  };

  const { data: allDentists } = useQuery(GET_ALL_USERS, {
    variables: {
      where: {
        type: {
          equals: UserType.DENTIST,
        },
        AND: [
          {
            OR: [
              ...searchDentistInputDebounce
                .trim()
                .split(" ")
                .reduce((acc, word: string) => {
                  acc.push(
                    ...[
                      {
                        firstName: {
                          contains: word,
                          mode: "insensitive",
                        },
                      },
                      {
                        lastName: {
                          contains: word,
                          mode: "insensitive",
                        },
                      },
                    ]
                  );
                  return acc;
                }, []),
            ],
          },
        ],
      },
      take: __TAKE__,
    },
  });

  const { data: allMaterialTraceability } = useQuery(
    GET_ALL_MATERIAL_TRACEABILITY_WHERE,
    {
      variables: {
        where: {
          providerMaterialTraceability: {
            some: {
              userGroupId: {
                equals: user?.userGroupId,
              },
            },
          },
        },
      },
    }
  );

  const getAllUntranslatedComment = () => {
    return (
      (order?.orderComment ?? []).filter(
        comment => !comment.translatedComment && comment.comment
      ) ?? []
    );
  };

  const createPickup = async (
    orderId: string,
    orderReference: string,
    data: {
      clinicClosingTime?: string;
      packageReadyTime?: string;
      date: string;
      comment: string | null;
      selectedCarrier: CarrierType;
      trackingNumber?: string;
    }
  ) => {
    try {
      if (
        !isIntegratedCarrier(
          data.selectedCarrier,
          ShippingType.PICKUP,
          user.email
        )
      ) {
        await createManualPickup({
          variables: {
            orderId: orderId,
            scheduledDate: data.date,
            comment: data.comment,
            trackingNumber: data.trackingNumber,
          },
        });
        refetch();
        setIsCreatePickupModalOpened(false);
        notifications.show({
          title: "Pickup created successfully",
          color: "green",
          message: "",
        });
        return;
      }
      notifications.show({
        title: "Downloading pickup label..",
        color: "blue",
        message: "",
        loading: true,
        id: "downloading-pickup-label",
        autoClose: false,
      });
      const req = await createPickupMutation({
        variables: {
          orderId: orderId,
          clinicClosingTime: data.clinicClosingTime,
          packageReadyTime: data.packageReadyTime,
          scheduledDate: data.date,
          comment: data.comment,
        },
      });
      downloadBase64File(
        req.data.createPickup,
        `pickup-label-${orderReference}`,
        ".pdf"
      );
      notifications.hide("downloading-pickup-label");
      refetch();
      setIsCreatePickupModalOpened(false);
      notifications.show({
        title: "Pickup label downloaded successfully",
        color: "green",
        message: "",
      });
    } catch (e) {
      console.error(e);
      notifications.hide("downloading-pickup-label");
      notifications.show({
        title: "Error while trying to download pickup label",
        color: "red",
        message: "",
      });
    }
  };

  const archiveAndSetAsDelivered = async () => {
    try {
      await updateOrder({
        variables: {
          where: {
            id: order.id,
          },
          data: {
            status: {
              set: OrderStatus.DELIVERED,
            },
            isArchived: {
              set: true,
            },
          },
        },
      });
      refetch();
      notifications.show({
        title: "Order is correctly archived and set as delivered",
        color: "green",
        message: "",
      });
    } catch (e) {
      console.error(e);
      notifications.show({
        title: "Error while trying to archived order and set it as delivered",
        color: "red",
        message: "",
      });
    }
  };

  const downloadDeliveryProof = async (
    orderId: string,
    orderReference: string
  ): Promise<void> => {
    try {
      notifications.show({
        title: "Downloading delivery proof..",
        color: "blue",
        message: "",
        loading: true,
        id: "download-delivery-proof",
        autoClose: false,
      });
      const req = await getDeliveryProofGQL({
        variables: {
          orderId: orderId,
        },
      });
      downloadBase64File(
        req.data.getDeliveryProof,
        `delivery-proof-${orderReference}`,
        ".pdf"
      );
      notifications.hide("download-delivery-proof");
      notifications.show({
        title: "Delivery proof downloaded",
        color: "green",
        message: "",
      });
    } catch (e) {
      console.error(e);
      notifications.hide("download-delivery-proof");
      notifications.show({
        title: "Error while trying dowload delivery proof",
        color: "red",
        message: "",
      });
    }
  };

  const archiveAndSetAsNotDelievered = async () => {
    try {
      const sortedStatusList = [...order?.history]
        .filter(
          orderStatus =>
            orderStatus?.status &&
            !orderStatus.status.includes(OrderStatus.DELIVERED)
        )
        .sort((a, b) => a?.createdAt - b?.createdAt);
      await updateOrder({
        variables: {
          where: {
            id: order.id,
          },
          data: {
            status:
              sortedStatusList.length > 0
                ? {
                    set: sortedStatusList[sortedStatusList.length - 1].status,
                  }
                : undefined,
            isArchived: {
              set: false,
            },
          },
        },
      });
      refetch();
      notifications.show({
        title: `Order is correctly unarchived`,
        color: "green",
        message: "",
      });
    } catch (e) {
      console.error(e);
      notifications.show({
        title: "Error while trying to unarchived order",
        color: "red",
        message: "",
      });
    }
  };

  const startProduction = async (
    providerGroupId?: string,
    sendDeleteCaseEmail: boolean = false,
    previousProviderGroupId?: string
  ) => {
    try {
      await updateOrder({
        variables: {
          where: {
            id: order.id,
          },
          data: {
            status: {
              set: providerGroupId
                ? OrderStatus.WAITING_FOR_PRODUCTION
                : OrderStatus.PRODUCING,
            },
            isArchived: {
              set: false,
            },
            provider: providerGroupId
              ? {
                  connect: {
                    id: providerGroupId,
                  },
                }
              : undefined,
          },
        },
      });
      refreshPastille({
        variables: {
          args: [
            {
              userGroupId: providerGroupId,
              previousUserGroupId: previousProviderGroupId,
              orderId: order.id,
              previousStatus: order.status,
              currentStatus: providerGroupId
                ? OrderStatus.WAITING_FOR_PRODUCTION
                : OrderStatus.PRODUCING,
            },
          ],
        },
      });
      refetch();
      if (providerGroupId) {
        await sendMail({
          variables: {
            args: {
              orderId: order.id,
              sendDeleteCaseEmail: sendDeleteCaseEmail,
              previousProviderGroupId: previousProviderGroupId,
            },
          },
        });

        if (
          previousProviderGroupId &&
          previousProviderGroupId !== providerGroupId
        ) {
          await updateChat({
            variables: {
              where: {
                orderId: {
                  equals: order.id,
                },
              },
              data: {
                archivedForProvider: {
                  set: true,
                },
              },
            },
          });
        }
      }
      notifications.show({
        title: "Order is correctly set as producing",
        color: "green",
        message: "",
      });
    } catch (e) {
      console.error(e);
      notifications.show({
        title: "Error while trying to set order as producing",
        color: "red",
        message: "",
      });
    }
  };

  const markAsProducingInTransit = async () => {
    if (order.status === OrderStatus.PRODUCING_IN_TRANSIT) {
      notifications.show({
        title: "Order is already in PRODUCING_IN_TRANSIT status",
        color: "red",
        message: "",
      });
      return;
    }
    if (order.status !== OrderStatus.PRODUCING) {
      notifications.show({
        title:
          "Cannot set order as PRODUCING_IN_TRANSIT cause status is not in PRODUCING state",
        color: "red",
        message: "",
      });
      return;
    }
    try {
      await updateOrder({
        variables: {
          where: {
            id: order.id,
          },
          data: {
            status: {
              set: OrderStatus.PRODUCING_IN_TRANSIT,
            },
          },
        },
      });
      refetch();
      notifications.show({
        title: "Order is correctly set as producting in transit",
        color: "green",
        message: "",
      });
    } catch (e) {
      console.error(e);
      notifications.show({
        title: "Error while trying to set order as producting in transit",
        color: "red",
        message: "",
      });
    }
  };

  const markOrderAsShipped = async () => {
    if (order.status === OrderStatus.SHIPPED) {
      notifications.show({
        title: "Order is already in SHIPPED status",
        color: "red",
        message: "",
      });
      return;
    }
    if (order.status !== OrderStatus.PRODUCING_IN_TRANSIT) {
      notifications.show({
        title:
          "Cannot set order as SHIPPED cause status is not in PRODUCING_IN_TRANSIT state",
        color: "red",
        message: "",
      });
      return;
    }
    try {
      await updateOrder({
        variables: {
          where: {
            id: order.id,
          },
          data: {
            status: {
              set: OrderStatus.SHIPPED,
            },
          },
        },
      });
      refetch();
      notifications.show({
        title: "Order is correctly set as SHIPPED",
        color: "green",
        message: "",
      });
    } catch (e) {
      console.error(e);
      notifications.show({
        title: "Error while trying to set order as SHIPPED",
        color: "red",
        message: "",
      });
    }
  };

  const setOrderOnHold = async () => {
    try {
      await updateOrder({
        variables: {
          where: {
            id: order.id,
          },
          data: {
            status: {
              set: OrderStatus.ON_HOLD,
            },
          },
        },
      });
      refetch();
      notifications.show({
        title: "Order is correctly set as ON_HOLD",
        color: "green",
        message: "",
      });
    } catch (e) {
      console.error(e);
      notifications.show({
        title: "Error while trying to set order ON_HOLD",
        color: "red",
        message: "",
      });
    }
  };

  const markAsDelivered = async () => {
    try {
      await updateOrder({
        variables: {
          where: {
            id: order.id,
          },
          data: {
            status: {
              set: OrderStatus.DELIVERED,
            },
          },
        },
      });
      refetch();
      notifications.show({
        title: "Order is correctly set as delivered",
        color: "green",
        message: "",
      });
    } catch (e) {
      console.error(e);
      notifications.show({
        title: "Error while trying to set order as delivered",
        color: "red",
        message: "",
      });
    }
  };

  const getTextForArchivedButton = order => {
    if (order.status !== OrderStatus.DELIVERED && order.isArchived === true) {
      return "Set order as delivered";
    }
    if (order.status === OrderStatus.DELIVERED && order.isArchived === false) {
      return "Archive order";
    }
    return "Archive & set as delivered";
  };

  const regenerateTraceabilitySheet = async () => {
    try {
      (order?.orderBilling ?? []).map(async (orderBilling: { id: string }) => {
        await generateTracebilitySheet({
          variables: {
            updateOrderBillingWhere: {
              id: orderBilling.id,
            },
            providerReference: "SSF1",
            orderBillingWhere: {
              id: orderBilling.id,
            },
          },
        });
      });
      const validFiles = getValideFiles(order?.files);
      notifications.show({
        title: "Loading",
        color: "grey",
        message: "",
        autoClose: 7000,
      });
      // We use setTimeout to let the server enouth time to create and upload the file on s3
      setTimeout(async () => {
        await refetch();
      }, 7000);
      setTimeout(() => {
        notifications.show({
          title: "Traceability sheet regenerated successfully",
          color: "green",
          message: "",
        });
      }, 10000);
    } catch (e) {
      console.error(e);
      notifications.show({
        title:
          "There was an error while trying to regenerate traceability sheet",
        color: "red",
        message: "",
      });
    }
  };

  const updateOrderStatus = async ({
    nextOrderStatus,
    previousOrderStatus,
    hasProviderAssignated,
  }: {
    nextOrderStatus: OrderStatus;
    previousOrderStatus: OrderStatus;
    hasProviderAssignated: boolean;
  }) => {
    if (
      willActivateOrder({
        previousOrderStatus: previousOrderStatus,
        nextOrderStatus: nextOrderStatus,
      }) &&
      !hasProviderAssignated
    ) {
      setOpenSendToSupplierModal(true);
      return;
    }

    try {
      await updateOrder({
        variables: {
          where: {
            id: order.id,
          },
          data: {
            status: {
              set: nextOrderStatus,
            },
            confirmedAt:
              nextOrderStatus === OrderStatus.CONFIRMED
                ? {
                    set: Date.now(),
                  }
                : undefined,
          },
        },
      });
      refetch();
      notifications.show({
        title: "Order status correctly updated",
        color: "green",
        message: "",
      });
    } catch (e) {
      console.error(e);
      notifications.show({
        title: "Error while trying to update order status",
        color: "red",
        message: "",
      });
    }
  };

  const displayDeliveryProofButton = (order: Order): boolean => {
    return (
      order.status === OrderStatus.DELIVERED &&
      hasDeliveryTrackingNumber(order?.shippings)
    );
  };

  const displayCarrierManagementButton = (order: any, user: User): boolean => {
    if (!isUserStaff(user)) {
      return false;
    }

    if (
      TOGGLE_FEATURE.INTEGRATED_PICKUP_CARRIERS(user.email).includes(
        order?.user?.userGroup?.pickupCarrier
      ) ||
      TOGGLE_FEATURE.INTEGRATED_DELIVERY_CARRIERS(user.email).includes(
        order?.user?.userGroup?.deliveryCarrier
      )
    ) {
      return true;
    }

    return false;
  };

  const displayOrderDesignButton = isUserStaff(user);

  const showMaterialTraceabilitySelectionButton =
    isUserProvider(user) &&
    hasAtLeastOneMaterialTraceability(
      order?.products,
      allMaterialTraceability?.getMaterialTraceabilityWhere ?? []
    );

  const handleSendBackToChina = async () => {
    const today = new Date();

    // update date into the order
    await updateOrder({
      variables: {
        where: { id: order.id },
        data: {
          shippedBackToProviderDate: { set: today.toISOString() },
        },
      },
    });

    // send message in the chat
    if (order?.provider?.id) {
      await sendChatMessage({
        variables: {
          args: {
            order: { connect: { id: order.id } },
            userGroup: { connect: { id: user?.userGroupId } },
            message: "We are sending this case back to you today",
          },
          olderChatRead: true,
          supplier: order.provider.id,
        },
      });
    }

    setShippedBackDate(today);
    refetch();
  };

  const isPacRdv2Order = (): boolean => {
    return (
      order?.products?.some(
        product =>
          (product.productType === ItemType.FULL_DENTURE ||
            product.productType === ItemType.PARTIAL_DENTURE) &&
          product.removableItem?.some(item => item.rdvNumber === 2)
      ) ?? false
    );
  };

  return (
    <div style={{ paddingBottom: "1rem", float: "right" }}>
      <Notifications
        position="bottom-center"
        pauseResetOnHover="notification"
      />
      <Modal
        opened={openModalOrderHistory}
        onClose={() => setOpenModalOrderHistory(false)}
      >
        <>
          {[...order?.history]
            .sort((a, b) => a?.createdAt - b?.createdAt)
            .map(history => (
              <OrderHistoryItem
                key={history?.id ?? `${history?.createdAt}-${history?.status}`}
                history={history}
              />
            ))}
        </>
      </Modal>
      <Modal
        opened={isPickupHistoryModalOpened}
        onClose={() => setIsPickupHistoryModalOpened(false)}
        size="auto"
      >
        <PickupHistory
          data={order?.pickup ?? []}
          abortPickup={abortPickup}
          pickupCarrier={order?.user?.userGroup?.pickupCarrier}
        />
      </Modal>
      <Modal
        opened={isCreatePickupModalOpened}
        onClose={() => setIsCreatePickupModalOpened(false)}
        size="auto"
      >
        <CreatePickup
          onSubmit={createPickup}
          orderId={order?.id}
          orderReference={order?.orderReference}
          lang={TranslationsAvailable.EN}
          pickupCarrier={order?.user?.userGroup?.pickupCarrier}
          pickupAvailabilities={pickupAvailable?.getPickupAvailability ?? []}
          connectedUserEmail={user.email}
        />
      </Modal>
      <Modal
        opened={isCarrierDeliveryModalOpened}
        onClose={() => setIsCarrierDeliveryModalOpened(false)}
        size="auto"
      >
        <CarrierDeliveryInfo
          orderId={order?.id}
          deliveryCarrier={order?.user?.userGroup?.deliveryCarrier}
        />
      </Modal>
      <Modal
        opened={openMaterialAndLotModal}
        onClose={() => setOpenMaterialAndLotModal(false)}
      >
        <UpdateMaterialAndLotModal
          order={order}
          closeModal={() => setOpenMaterialAndLotModal(false)}
          refetch={refetch}
          allMaterialTraceability={
            allMaterialTraceability?.getMaterialTraceabilityWhere ?? []
          }
          user={user}
        />
      </Modal>
      <Modal
        opened={openDeliveryDateModal}
        onClose={() => setOpenDeliveryDateModal(false)}
        size="auto"
      >
        <UpdateDeliveryDateModal
          order={order}
          closeModal={() => setOpenDeliveryDateModal(false)}
          refetch={refetch}
        />
      </Modal>
      <Modal
        opened={isShippingHistoryModalOpened}
        onClose={() => setIsShippingHistoryModalOpened(false)}
      >
        <ShippingHistory shippings={order?.shippings ?? []} />
      </Modal>
      <Modal opened={openRedoModal} onClose={() => setOpenRedoModal(false)}>
        <RedoModal
          order={order}
          closeModal={() => setOpenRedoModal(false)}
          refetch={refetch}
        />
      </Modal>
      <Modal
        opened={changeDentistModalOpened}
        onClose={() => setChangeDentistModalOpened(false)}
      >
        <ChangeDentistModal
          allDentists={(allDentists?.users ?? []).map(user => ({
            value: user?.id,
            label: `${user?.firstName} ${user?.lastName} - ${user?.isManager ? "MASTER ACCOUNT -" : ""} (${user?.email})`,
          }))}
          setSearchDentistInput={value => setSearchDentistInput(value)}
          searchDentistInput={searchDentistInput}
          onSubmit={async dentistId => {
            const orderBillingId =
              (order?.orderBilling ?? []).length > 0
                ? order?.orderBilling[0]?.id
                : undefined;
            try {
              await updateOrder({
                variables: {
                  where: {
                    id: order?.id,
                  },
                  data: {
                    user: {
                      connect: {
                        id: dentistId,
                      },
                    },
                    orderBilling: {
                      update: [
                        {
                          data: {
                            user: {
                              connect: {
                                id: dentistId,
                              },
                            },
                          },
                          where: {
                            id: orderBillingId,
                          },
                        },
                      ],
                    },
                  },
                },
              });
              notifications.show({
                title: "Dentist updated",
                color: "green",
                message: "",
              });
              setChangeDentistModalOpened(false);
              refetch();
            } catch (e) {
              notifications.show({
                title: "Error while trying to update linked to this order",
                color: "red",
                message: "",
              });
              console.error(e);
            }
          }}
        />
      </Modal>
      <Modal
        opened={openSendToSupplierModal}
        onClose={() => setOpenSendToSupplierModal(false)}
      >
        <SendOrderToSupplierModal
          suppliers={(order?.user?.userGroup?.availableProvider ?? [])
            .filter(
              (availableProvider: { provider: { id: string } }) =>
                availableProvider?.provider?.id !== order?.provider?.id
            )
            .map(
              (availableProvider: {
                provider: {
                  id: string;
                  name: string;
                  displayPriority: number | null;
                };
              }) => {
                return {
                  id: availableProvider?.provider.id,
                  name: availableProvider?.provider.name,
                  displayPriority: availableProvider?.provider.displayPriority,
                };
              }
            )}
          onSubmit={(selectedSupplierId: string) => {
            if (!selectedSupplierId) {
              notifications.show({
                title: "You must select a provider",
                color: "red",
                message: "",
              });
              return;
            }
            startProduction(
              selectedSupplierId,
              order.status === OrderStatus.PRODUCING ||
                order.status === OrderStatus.WAITING_FOR_PRODUCTION,
              order?.provider?.id ?? undefined
            );
            setOpenSendToSupplierModal(false);
          }}
        />
      </Modal>
      <UploadDesignModal
        key={order.id}
        isOpen={isUploadDesignModalOpen}
        onClose={() => setIsUploadDesignModalOpen(false)}
        orderId={order.id}
      />
      <Flex wrap="wrap" gap="xs">
        {isUserStaff(user) &&
          (order.status === OrderStatus.PRODUCING ||
            order.status === OrderStatus.WAITING_FOR_PRODUCTION) && (
            <Button
              onClick={() => setOpenSendToSupplierModal(true)}
              style={{ backgroundColor: STYLE.primary }}
            >
              <IconTransfer style={{ marginRight: "5px" }} /> Send order to
              another suppliers
            </Button>
          )}
        {isUserStaff(user) &&
          (order.status === OrderStatus.CONFIRMED ||
            order.status === OrderStatus.ON_HOLD ||
            order.status === OrderStatus.NEED_TO_BE_PROCESSED ||
            order.status === OrderStatus.DELIVERED) && (
            <>
              <Tooltip label="You need to translated all comments before start production">
                <Button
                  onClick={() => setOpenSendToSupplierModal(true)}
                  disabled={getAllUntranslatedComment().length > 0}
                  style={() =>
                    getAllUntranslatedComment().length > 0
                      ? { backgroundColor: "lightgrey" }
                      : { backgroundColor: STYLE.primary }
                  }
                >
                  <IconArrowLeftFromArc style={{ marginRight: "5px" }} /> Send
                  order to suppliers
                </Button>
              </Tooltip>
            </>
          )}
        {isUserStaff(user) && (
          <ButtonMenu
            title={
              <>
                <IconTool style={{ marginRight: "5px" }} /> Order tools
              </>
            }
            data={[
              {
                onClick: () => setChangeDentistModalOpened(true),
                content: (
                  <>
                    <IconSortAscendingShapes
                      style={{ marginRight: "5px", verticalAlign: "bottom" }}
                    />{" "}
                    Change dentist
                  </>
                ),
              },
              ...(order.isArchived
                ? [
                    {
                      onClick: archiveAndSetAsNotDelievered,
                      content: (
                        <>
                          <IconArchiveOff
                            style={{
                              marginRight: "5px",
                              verticalAlign: "bottom",
                            }}
                          />{" "}
                          Unarchive order
                        </>
                      ),
                    },
                  ]
                : []),
              {
                onClick: () => setOpenModalOrderHistory(true),
                content: (
                  <>
                    <IconLogs
                      style={{ marginRight: "5px", verticalAlign: "bottom" }}
                    />{" "}
                    Show order history
                  </>
                ),
              },
              {
                onClick: () => regenerateTraceabilitySheet(),
                content: (
                  <>
                    <IconArrowIteration
                      style={{ marginRight: "5px", verticalAlign: "bottom" }}
                    />{" "}
                    Regenerate traceability sheet
                  </>
                ),
              },
              ...(isPacRdv2Order()
                ? [
                    {
                      onClick: handleSendBackToChina,
                      content: (
                        <>
                          <IconSend
                            style={{
                              marginRight: "5px",
                              verticalAlign: "bottom",
                            }}
                          />{" "}
                          Send back to China
                        </>
                      ),
                    },
                  ]
                : []),
              ...(order.integrationType === IntegrationTypeEnum.THREESHAPE
                ? [
                    {
                      onClick: async () =>
                        await populateExternalData({
                          orderId: order.id,
                          integrationType: IntegrationTypeEnum.THREESHAPE,
                        }),
                      content: (
                        <>
                          <IconCloudDown
                            style={{
                              marginRight: "5px",
                              verticalAlign: "bottom",
                            }}
                          />{" "}
                          Download data from 3shape communicate
                        </>
                      ),
                    },
                  ]
                : []),
              ...(order.integrationType === IntegrationTypeEnum.MEDITLINK
                ? [
                    {
                      onClick: async () =>
                        await populateExternalData({
                          orderId: order.id,
                          integrationType: IntegrationTypeEnum.MEDITLINK,
                        }),
                      content: (
                        <>
                          <IconCloudDown
                            style={{
                              marginRight: "5px",
                              verticalAlign: "bottom",
                            }}
                          />{" "}
                          Download data from MeditLink
                        </>
                      ),
                    },
                  ]
                : []),
            ]}
          />
        )}
        {isUserStaff(user) && (
          <ButtonMenu
            title={
              <>
                <IconStatusChange style={{ marginRight: "5px" }} /> Update order
                status
              </>
            }
            data={Object.keys(OrderStatus).map((status: OrderStatus) => {
              return {
                onClick: () =>
                  updateOrderStatus({
                    nextOrderStatus: status,
                    previousOrderStatus: order.status,
                    hasProviderAssignated: !!order?.providerGroupId,
                  }),
                content: status,
              };
            })}
          />
        )}
        {isUserProvider(user) && order.status === OrderStatus.ON_HOLD && (
          <Button
            onClick={() => startProduction()}
            style={{ backgroundColor: STYLE.primary }}
          >
            <IconProgress style={{ marginRight: "5px" }} /> order back to
            producing
          </Button>
        )}
        {isUserProvider(user) &&
          (order.status === OrderStatus.WAITING_FOR_PRODUCTION ||
            order.status === OrderStatus.PRODUCING) && (
            <Button
              onClick={() => setOrderOnHold()}
              style={{ backgroundColor: STYLE.primary }}
            >
              <IconHandStop style={{ marginRight: "5px" }} /> Set order on hold
            </Button>
          )}
        {(isUserStaff(user) || isUserProvider(user)) &&
          (order.status === OrderStatus.WAITING_FOR_PRODUCTION ||
            order.status === OrderStatus.PRODUCING) && (
            <Button
              onClick={() => setIsUploadDesignModalOpen(true)}
              style={{ backgroundColor: STYLE.primary }}
            >
              <IconFile3d style={{ marginRight: "5px" }} /> Upload model for
              review
            </Button>
          )}
        {showMaterialTraceabilitySelectionButton && (
          <Button
            onClick={() => setOpenMaterialAndLotModal(true)}
            style={{ backgroundColor: STYLE.primary }}
          >
            <IconListDetails style={{ marginRight: "5px" }} /> Add material list
            and lot number
          </Button>
        )}
        {isUserStaff(user) &&
          order.status === OrderStatus.PRODUCING_IN_TRANSIT && (
            <Button
              onClick={markOrderAsShipped}
              style={{ backgroundColor: STYLE.primary }}
            >
              <IconPlaneDeparture style={{ marginRight: "5px" }} /> Mark as
              shipped from France to France
            </Button>
          )}
        {isUserStaff(user) && (
          <Button
            onClick={() => navigate(`/orders/${order.id}/edit/`)}
            style={{ backgroundColor: STYLE.primary }}
          >
            <IconEdit style={{ marginRight: "5px" }} /> Edit products and items
          </Button>
        )}
        {isUserStaff(user) &&
          order.status !== OrderStatus.DELIVERED &&
          order.status !== OrderStatus.DRAFT && (
            <Button
              onClick={() => setOpenDeliveryDateModal(true)}
              style={{ backgroundColor: STYLE.primary }}
            >
              <IconCalendarCog style={{ marginRight: "5px" }} /> Update delivery
              date
            </Button>
          )}
        {isUserStaff(user) && order.status === OrderStatus.DELIVERED && (
          <Button
            onClick={() => setOpenRedoModal(true)}
            style={{ backgroundColor: STYLE.primary }}
          >
            <IconRepeat style={{ marginRight: "5px" }} /> Mark as redo
          </Button>
        )}
        {isUserProvider(user) &&
          order.status === OrderStatus.WAITING_FOR_PRODUCTION && (
            <Button
              onClick={() => startProduction()}
              style={{ backgroundColor: STYLE.primary }}
            >
              <IconProgress style={{ marginRight: "5px" }} /> Start production
            </Button>
          )}
        {isUserProvider(user) && order.status === OrderStatus.PRODUCING && (
          <Button
            onClick={markAsProducingInTransit}
            style={{ backgroundColor: STYLE.primary }}
          >
            <IconPlaneDeparture style={{ marginRight: "5px" }} /> Mark as
            shipped to France
          </Button>
        )}
        {isUserStaff(user) && order.status === OrderStatus.SHIPPED && (
          <Button
            onClick={markAsDelivered}
            style={{ backgroundColor: STYLE.primary }}
          >
            <IconMailbox style={{ marginRight: "5px" }} /> Mark as delivered
          </Button>
        )}
        {isUserStaff(user) &&
          (order.status !== OrderStatus.DELIVERED ||
            order.isArchived === false) && (
            <Button
              onClick={archiveAndSetAsDelivered}
              style={{ backgroundColor: STYLE.primary }}
            >
              <IconArchive style={{ marginRight: "5px" }} />{" "}
              {getTextForArchivedButton(order)}
            </Button>
          )}
        {isUserProvider(user) && (
          <Button
            style={{ backgroundColor: STYLE.primary }}
            onClick={() => {
              window.print();
            }}
          >
            <IconPrinter style={{ marginRight: "5px" }} /> Print
          </Button>
        )}
        {displayCarrierManagementButton(order, user) && (
          <ButtonMenu
            title={
              <>
                <IconTruckLoading style={{ marginRight: "5px" }} /> Carrier
              </>
            }
            data={[
              ...(TOGGLE_FEATURE.INTEGRATED_PICKUP_CARRIERS(
                user.email
              ).includes(order?.user?.userGroup?.pickupCarrier)
                ? [
                    {
                      onClick: () => setIsCreatePickupModalOpened(true),
                      content: (
                        <>
                          <IconTruckReturn
                            style={{
                              marginRight: "5px",
                              verticalAlign: "bottom",
                            }}
                          />{" "}
                          Create pickup
                        </>
                      ),
                    },
                  ]
                : []),
              ...(displayDeliveryProofButton(order)
                ? [
                    {
                      onClick: () =>
                        downloadDeliveryProof(order?.id, order?.orderReference),
                      content: (
                        <>
                          <IconCloudDown
                            style={{
                              marginRight: "5px",
                              verticalAlign: "bottom",
                            }}
                          />{" "}
                          Download delivery proof
                        </>
                      ),
                    },
                  ]
                : []),
              ...(hasPickupTrackingNumber(
                order?.shippings ?? [],
                order?.pickup ?? []
              )
                ? [
                    {
                      onClick: () => setIsPickupHistoryModalOpened(true),
                      content: (
                        <>
                          <IconHistory
                            style={{
                              marginRight: "5px",
                              verticalAlign: "bottom",
                            }}
                          />{" "}
                          View pickup history
                        </>
                      ),
                    },
                  ]
                : []),
              ...(displayDeliveryStatusButton(
                order?.user?.userGroup?.deliveryCarrier,
                order?.status,
                hasDeliveryTrackingNumber(order.shippings)
              )
                ? [
                    {
                      onClick: () => setIsCarrierDeliveryModalOpened(true),
                      content: (
                        <>
                          <IconChecklist
                            style={{
                              marginRight: "5px",
                              verticalAlign: "bottom",
                            }}
                          />
                          See delivery details
                        </>
                      ),
                    },
                  ]
                : []),

              ...(displayDeliveryStatusButton(
                order?.user?.userGroup?.deliveryCarrier,
                order?.status,
                !!lastTrackingNumber
              )
                ? [
                    {
                      onClick: () => setIsShippingHistoryModalOpened(true),
                      content: (
                        <>
                          <IconList
                            style={{
                              marginRight: "5px",
                              verticalAlign: "bottom",
                            }}
                          />
                          Show tracking number history
                        </>
                      ),
                    },
                  ]
                : []),
            ]}
          />
        )}
        {displayOrderDesignButton && (
          <Button
            onClick={() => navigate(`/orders/${order.id}/designs/`)}
            style={{ backgroundColor: STYLE.primary }}
          >
            <IconFile3d style={{ marginRight: "5px" }} /> Order Designs
          </Button>
        )}
        <Button
          onClick={() => downloadBagLabel(order)}
          style={{ backgroundColor: STYLE.primary }}
        >
          <IconTagsFilled style={{ marginRight: "5px" }} /> Download bag label
        </Button>
      </Flex>
    </div>
  );
};

export default HeaderOrderView;
